import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FaPlus,
  FaEllipsisVertical,
  FaCircleCheck,
  FaClock,
} from "react-icons/fa6";
import { FiFileText } from "react-icons/fi";
import { FaSearch } from "react-icons/fa";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { BtnBack } from "../../../../../components/buttons/BtnBack";

/* ============================================
   Helpers
   ============================================ */
const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateString) => {
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateString).toLocaleDateString("id-ID", options);
};

/* ============================================
   KOMPONEN: Badge Status
   ============================================ */
const StatusBadge = ({ status }) => {
  const isPaid = status === "paid" || status === "Lunas";

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${isPaid
          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
          : "bg-amber-500/10 text-amber-400 border-amber-500/20"
        }`}
    >
      {isPaid ? <FaCircleCheck /> : <FaClock />}
      {isPaid ? "Lunas" : "Belum Lunas"}
    </div>
  );
};

/* ============================================
   KOMPONEN: ActionMenu (Dropdown Sederhana)
   ============================================ */
const ActionMenu = ({ onPay, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
      >
        <FaEllipsisVertical />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="absolute right-0 mt-2 w-36 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-20 overflow-hidden">
            <button
              onClick={() => {
                onPay();
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors flex items-center gap-2"
            >
              <FaCircleCheck className="text-emerald-400" /> Bayar
            </button>
            <button
              onClick={() => {
                onDelete();
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-xs text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors flex items-center gap-2"
            >
              Hapus
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ============================================
   KOMPONEN: StatCardMini (Khusus Halaman Hutang)
   ============================================ */
const StatCardMini = ({ label, value, color, subtext }) => (
  <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/50 backdrop-blur-sm flex flex-col justify-between h-full">
    <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
      {label}
    </span>
    <div>
      <h4 className={`text-lg font-bold text-gray-100 ${color}`}>{value}</h4>
      <p className="text-[10px] text-slate-500 mt-1">{subtext}</p>
    </div>
  </div>
);

/* ============================================
   MAIN COMPONENT: HutangList
   ============================================ */
export const Hutang = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [debts, setDebts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  // Mock Data Hutang
  const mockDebts = [
    {
      id: 1,
      creditor: "PT. Telkom Indonesia",
      amount: 5500000,
      dueDate: "2023-11-25",
      status: "unpaid",
      description: "Tagihan Internet Bulan November",
    },
    {
      id: 2,
      creditor: "CV. Maju Jaya",
      amount: 1200000,
      dueDate: "2023-10-30",
      status: "paid",
      description: "Pembayaran Material Kabel",
    },
    {
      id: 3,
      creditor: "Tokopedia PayLater",
      amount: 750000,
      dueDate: "2023-12-01",
      status: "unpaid",
      description: "Cicilan Router Mikrotik",
    },
    {
      id: 4,
      creditor: "Vendor Listrik",
      amount: 3200000,
      dueDate: "2023-11-10",
      status: "overdue", // Custom status logic handling
      description: "Biaya Maintenance Tower",
    },
  ];

  useEffect(() => {
    const u = localStorage.getItem("user_data");
    if (u) setUserData(JSON.parse(u));

    // Simulate Fetch
    setTimeout(() => {
      setDebts(mockDebts);
      setIsLoading(false);
    }, 800);
  }, []);

  // Filter Logic
  const filteredDebts = debts.filter((debt) => {
    const matchesSearch = debt.creditor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterStatus === "all"
        ? true
        : filterStatus === "paid"
          ? debt.status === "paid"
          : debt.status !== "paid";
    return matchesSearch && matchesFilter;
  });

  // Calculations for Stats
  const totalHutang = debts.reduce((acc, curr) => acc + curr.amount, 0);
  const totalLunas = debts
    .filter((d) => d.status === "paid")
    .reduce((acc, curr) => acc + curr.amount, 0);
  const sisaHutang = totalHutang - totalLunas;

  return (
    <>
      <Helmet>
        <title>Data Hutang | Danela Net</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* Header */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <BtnBack />
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Data Hutang
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5 uppercase tracking-[0.2em]">
                Kelola Kewajiban Keuangan
              </p>
            </div>
          </div>

          {/* Tombol Tambah */}
          <button
            onClick={() => console.log("Buka Modal Tambah Hutang")}
            className="hidden md:flex items-center gap-2 px-4 py-2 bg-ocean-600 hover:bg-ocean-500 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-ocean-900/20 active:scale-95"
          >
            <FaPlus /> Tambah Hutang
          </button>
        </Header>

        {/* Main Content */}
        <main className="p-4 lg:p-6 space-y-6">

          {/* Statistik Ringkas (Grid 3 Kolom) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCardMini
              label="Total Hutang"
              value={formatCurrency(totalHutang)}
              subtext="Semua Kewajiban"
            />
            <StatCardMini
              label="Sudah Dibayar"
              value={formatCurrency(totalLunas)}
              color="text-emerald-400"
              subtext="Hutang Lunas"
            />
            <StatCardMini
              label="Sisa Hutang"
              value={formatCurrency(sisaHutang)}
              color="text-rose-400"
              subtext="Perlu Dibayar"
            />
          </div>

          {/* Panel Kontrol & List */}
          <div className="rounded-2xl overflow-hidden bg-slate-800/30 border border-slate-700/50 backdrop-blur-sm">

            {/* Toolbar: Search & Filter */}
            <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="relative w-full sm:w-72">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
                <input
                  type="text"
                  placeholder="Cari pihak ketiga..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-700 text-slate-200 text-xs rounded-lg pl-9 pr-4 py-2.5 focus:outline-none focus:border-ocean-500 transition-colors placeholder-slate-600"
                />
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-slate-900/50 border border-slate-700 text-slate-300 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:border-ocean-500 cursor-pointer w-full sm:w-auto"
                >
                  <option value="all">Semua Status</option>
                  <option value="unpaid">Belum Lunas</option>
                  <option value="paid">Lunas</option>
                </select>
              </div>
            </div>

            {/* Table/List Body */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="p-8 flex justify-center">
                  <div className="w-8 h-8 border-2 border-ocean-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : filteredDebts.length > 0 ? (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-700/50">
                      <th className="px-6 py-4 font-medium">Pihak Ketiga</th>
                      <th className="px-6 py-4 font-medium">Total</th>
                      <th className="px-6 py-4 font-medium">Jatuh Tempo</th>
                      <th className="px-6 py-4 font-medium">Status</th>
                      <th className="px-6 py-4 font-medium text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm divide-y divide-slate-700/30">
                    {filteredDebts.map((debt) => (
                      <tr
                        key={debt.id}
                        className="group hover:bg-slate-700/20 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-100 text-sm">
                              {debt.creditor}
                            </span>
                            <span className="text-[10px] text-slate-500 mt-0.5">
                              {debt.description}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-300 font-mono text-sm">
                          {formatCurrency(debt.amount)}
                        </td>
                        <td className="px-6 py-4 text-slate-400 text-xs">
                          {formatDate(debt.dueDate)}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={debt.status} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ActionMenu
                            onPay={() => console.log(`Bayar hutang ID: ${debt.id}`)}
                            onDelete={() => console.log(`Hapus hutang ID: ${debt.id}`)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 flex flex-col items-center justify-center text-slate-500">
                  <FiFileText className="text-4xl mb-3 opacity-50" />
                  <p className="text-sm">Tidak ada data hutang ditemukan.</p>
                </div>
              )}
            </div>

            {/* Pagination / Footer Table */}
            <div className="p-4 border-t border-slate-700/50 flex justify-between items-center bg-slate-800/20">
              <span className="text-[10px] text-slate-500">
                Menampilkan {filteredDebts.length} data
              </span>
              <div className="flex gap-1">
                <button className="w-6 h-6 rounded bg-slate-700 text-slate-400 text-xs flex items-center justify-center hover:bg-slate-600 disabled:opacity-50" disabled>&lt;</button>
                <button className="w-6 h-6 rounded bg-slate-700 text-slate-400 text-xs flex items-center justify-center hover:bg-slate-600 disabled:opacity-50" disabled>&gt;</button>
              </div>
            </div>

          </div>

          {/* Floating Action Button for Mobile */}
          <button
            onClick={() => console.log("Buka Modal Tambah Hutang Mobile")}
            className="md:hidden fixed bottom-6 right-6 w-12 h-12 bg-ocean-600 rounded-full shadow-xl shadow-ocean-900/50 flex items-center justify-center text-white z-40"
          >
            <FaPlus size={20} />
          </button>

        </main>
      </DashboardLayout>
    </>
  );
};